import http from './http';

export interface User {
  id: string;
  username: string;
  name?: string | null;
  email: string;
  created_at: string;
  is_owner?: boolean;
  // True when a one-time support link was minted for this account; the user
  // MUST set a new password before reaching the app.
  must_reset_password?: boolean;
  // True when the user has a credential-reveal vault PIN set (never the PIN).
  has_credential_pin?: boolean;
}

export interface AuthResponse {
  ok: boolean;
  user: User;
  token: string;
  expires_in: number;
}

// Response shape of PUT /auth/password (the forced-reset flow enriches it with a
// fresh token + updated user so the client session can be refreshed).
export interface PasswordUpdateResponse {
  ok: boolean;
  message?: string;
  forced_reset: boolean;
  user: User;
  token: string;
  expires_in: number;
}

export const authApi = {
  register: (email: string, username: string, password: string, name?: string) =>
    http.post<AuthResponse>('/auth/register', { email, username, password, ...(name ? { name } : {}) }),

  login: (username: string, password: string) =>
    http.post<AuthResponse>('/auth/login', { username, password }),

  exchangeSupportLogin: (token: string) =>
    http.post<AuthResponse>('/auth/support-login', { token }),

  me: () => http.get<User>('/auth/me'),

  updatePassword: (current_password: string, new_password: string) =>
    http.put('/auth/password', { current_password, new_password }),

  // Forced reset after a one-time support login: no current password required.
  // Returns a fresh token + updated user (flag cleared).
  resetPassword: (new_password: string) =>
    http.put<PasswordUpdateResponse>('/auth/password', { new_password }),

  updateName: (name: string) =>
    http.put('/auth/name', { name }),

  // Credential-reveal vault PIN (step-up auth). First-time set needs no
  // current password; updating an existing PIN does require it.
  setCredentialPin: (pin: string, current_password?: string) =>
    http.put<{ ok: boolean; has_credential_pin: boolean; message?: string }>(
      '/auth/credential-pin', { pin, ...(current_password ? { current_password } : {}) }),
  // Removing the PIN always requires the current account password.
  clearCredentialPin: (current_password: string) =>
    http.delete<{ ok: boolean; has_credential_pin: boolean; message?: string }>(
      '/auth/credential-pin', { data: { current_password } }),
};
